/**
 * Client Types as defined by DGI Burkina Faso
 * Reference: Spécifications Techniques SECeF v2.0
 *
 * Client identification is required for invoices > 50,000 FCFA
 * or for all FV (Facture de Vente) type invoices
 */
/**
 * Client types as defined by DGI
 */
export const CLIENT_TYPES = {
    /**
     * Client Comptant - Cash client
     * Anonymous client, no identification required
     */
    CC: {
        code: "CC",
        label: "Client Comptant",
        invoiceMention: "[CC] Client comptant",
        requiresIFU: false,
        requiresName: false,
        acceptsNIF: false,
        requiredDocs: [],
        description: "Client anonyme sans identification requise",
    },
    /**
     * Personne Morale - Legal entity
     * Companies, associations, NGOs - requires Name and IFU
     */
    PM: {
        code: "PM",
        label: "Personne Morale",
        invoiceMention: "[PM] Personne morale",
        requiresIFU: true,
        requiresName: true,
        acceptsNIF: true,
        requiredDocs: ["Nom", "IFU"],
        description: "Société, association ou ONG avec IFU",
    },
    /**
     * Personne Physique - Individual
     * Private individuals - requires Name only
     */
    PP: {
        code: "PP",
        label: "Personne Physique",
        invoiceMention: "[PP] Personne physique",
        requiresIFU: false,
        requiresName: true,
        acceptsNIF: false,
        requiredDocs: ["Nom"],
        description: "Particulier identifié par son nom",
    },
    /**
     * Personne Physique Commerçant - Individual trader
     * Registered individual traders - requires Name and IFU
     */
    PC: {
        code: "PC",
        label: "Personne Physique Commerçant",
        invoiceMention: "[PC] Personne physique commerçant",
        requiresIFU: true,
        requiresName: true,
        acceptsNIF: false,
        requiredDocs: ["Nom", "IFU"],
        description: "Commerçant individuel enregistré avec IFU",
    },
};
/**
 * IFU (Identifiant Fiscal Unique) format for Burkina Faso
 *
 * Format: 8 digits + 1 check letter (A-Z)
 * Example: 00012345A, 11111111B
 *
 * Note: The official check letter algorithm is not publicly documented.
 * We validate only the format pattern, not the check letter validity.
 */
export const IFU_PATTERN_BF = /^[0-9]{8}[A-Z]$/;
/**
 * Legacy IFU pattern (8-13 alphanumeric characters)
 * Used for foreign entities or older formats
 */
export const IFU_PATTERN_LEGACY = /^[A-Z0-9]{8,13}$/;
/**
 * Get client type by code
 */
export function getClientType(code) {
    return CLIENT_TYPES[code];
}
/**
 * Validate IFU format
 *
 * For Burkina Faso entities (9 characters: 8 digits + 1 letter):
 * - Validates format pattern only (8 digits followed by letter A-Z)
 * - Does NOT validate check letter (algorithm not publicly documented)
 *
 * For foreign entities or legacy formats (8-13 alphanumeric):
 * - Only validates basic format
 *
 * @param ifu - The IFU to validate
 * @param options - Validation options
 * @returns Validation result with details
 */
export function validateIFU(ifu, options) {
    const normalized = ifu.toUpperCase().trim();
    // Check Burkina Faso format first (most common case)
    if (IFU_PATTERN_BF.test(normalized)) {
        return {
            valid: true,
            isBurkinaFaso: true,
            normalized,
        };
    }
    // Check legacy/foreign format
    if (IFU_PATTERN_LEGACY.test(normalized)) {
        // In strict mode, only accept BF format
        if (options?.strict) {
            return {
                valid: false,
                isBurkinaFaso: false,
                error: "Format IFU étranger non accepté en mode strict",
                normalized,
            };
        }
        return {
            valid: true,
            isBurkinaFaso: false,
            normalized,
        };
    }
    return {
        valid: false,
        isBurkinaFaso: false,
        error: "Format IFU invalide (attendu: 8 chiffres + 1 lettre de contrôle)",
        normalized,
    };
}
/**
 * Validate IFU format (simple boolean check)
 *
 * @param ifu - The IFU to validate
 * @returns true if the IFU format is valid
 */
export function isValidIFU(ifu) {
    return validateIFU(ifu).valid;
}
/**
 * Validate IFU for Burkina Faso entities (format check only)
 *
 * Note: This validates the format pattern (8 digits + letter) but cannot
 * verify the check letter since the algorithm is not publicly documented.
 *
 * @param ifu - The IFU to validate
 * @returns true if the IFU matches BF format pattern
 */
export function isValidBurkinaFasoIFU(ifu) {
    const result = validateIFU(ifu);
    return result.valid && result.isBurkinaFaso;
}
/**
 * Generate an IFU-formatted string for testing purposes
 *
 * WARNING: This generates a format-valid IFU but the check letter is random.
 * Real IFU validation requires the official DGI algorithm which is not public.
 * Use only for development/testing, not for production data.
 *
 * @param digits - Optional 8 digits (default: random)
 * @returns An IFU-formatted string (8 digits + random letter A-Z)
 */
export function generateTestIFU(digits) {
    const ifuDigits = digits ?? String(Math.floor(Math.random() * 100000000)).padStart(8, "0");
    if (!/^[0-9]{8}$/.test(ifuDigits)) {
        throw new Error("IFU digits must be exactly 8 numeric characters");
    }
    // Use random letter since we don't know the real check letter algorithm
    const randomLetter = String.fromCharCode(65 + Math.floor(Math.random() * 26)); // A-Z
    return ifuDigits + randomLetter;
}
function clientTypeCandidates(tables) {
    return tables
        ? tables.clientTypes.map((row) => ({ code: row.code, ...row.attributes }))
        : Object.values(CLIENT_TYPES);
}
/**
 * Determine client type based on available information.
 *
 * The type is the first row (DGI rows first) whose requirements match what
 * the client carries: a company needs an IFU-bearing type that accepts a
 * NIF (PM); an IFU with a name needs an IFU-bearing type (PC); a name alone
 * a name-only type (PP); nothing an anonymous type (CC).
 */
export function determineClientType(client, tables) {
    const candidates = clientTypeCandidates(tables);
    const hasIFU = Boolean(client.ifu && isValidIFU(client.ifu));
    const hasName = Boolean(client.name);
    const pick = (match) => candidates.find(match)?.code;
    if (client.rccm || client.isCompany) {
        return (pick((type) => type.requiresIFU && type.requiresName && type.acceptsNIF) ??
            pick((type) => type.requiresIFU && type.requiresName) ??
            "PM");
    }
    if (hasIFU && hasName) {
        return (pick((type) => type.requiresIFU && type.requiresName && !type.acceptsNIF) ??
            pick((type) => type.requiresIFU && type.requiresName) ??
            "PC");
    }
    if (hasName) {
        return pick((type) => type.requiresName && !type.requiresIFU) ?? "PP";
    }
    return pick((type) => !type.requiresName && !type.requiresIFU) ?? "CC";
}
/**
 * Check if client identification is complete for the given type:
 * a name when the type requires one, a valid IFU when it requires one.
 * An unknown type is never complete.
 */
export function isClientIdentificationComplete(type, client, tables) {
    const candidate = clientTypeCandidates(tables).find((row) => row.code === type);
    if (!candidate) {
        return false;
    }
    if (candidate.requiresName && !client.name) {
        return false;
    }
    if (candidate.requiresIFU && !(client.ifu && isValidIFU(client.ifu))) {
        return false;
    }
    return true;
}
/**
 * Get all client types that require IFU
 */
export function getIFURequiredTypes() {
    return Object.values(CLIENT_TYPES).filter((t) => t.requiresIFU);
}
/**
 * Get all client types that require name
 */
export function getNameRequiredTypes() {
    return Object.values(CLIENT_TYPES).filter((t) => t.requiresName);
}
//# sourceMappingURL=client-types.js.map